import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../components/Layout";
import { playAmbient, stopAmbient } from "../lib/ambient";
import { BRAND } from "../lib/brand";
import { container, item } from "../lib/motion";
import { Play, Pause, RotateCcw, Wind, Timer as TimerIcon, Maximize, X, CloudRain, Trees, Waves, Volume2, VolumeX } from "lucide-react";

const TABS = [
  { id: "meditate", label: "Meditation", icon: TimerIcon },
  { id: "breathe", label: "Breathing", icon: Wind },
  { id: "focus", label: "Focus Mode", icon: Maximize },
];
const SOUNDS = [
  { id: "calm", label: "Calm", icon: Volume2 },
  { id: "rain", label: "Rain", icon: CloudRain },
  { id: "forest", label: "Forest", icon: Trees },
  { id: "ocean", label: "Ocean", icon: Waves },
];
const PATTERNS = { "Box · 4-4-4-4": [4, 4, 4, 4], "Relax · 4-7-8": [4, 7, 8, 0], "Calm · 5-5-5-5": [5, 5, 5, 5] };
const PHASES = ["Inhale", "Hold", "Exhale", "Hold"];

export default function Tools() {
  const [tab, setTab] = useState("meditate");
  return (
    <Layout glow="default">
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-16">
        <motion.div initial="hidden" animate="show" variants={container} className="text-center mb-12">
          <motion.span variants={item} className="eyebrow">Mindfulness Tools</motion.span>
          <motion.h1 variants={item} className="font-head text-4xl lg:text-6xl font-light mt-4 tracking-tight">Find your center</motion.h1>
        </motion.div>
        <div className="flex justify-center gap-2.5 mb-14 flex-wrap">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tools-tab-${t.id}`}
              className={`px-5 py-2.5 rounded-full text-sm transition-all flex items-center gap-2 ${tab === t.id ? "bg-[#4da6ff] text-white glow-blue" : "btn-glass"}`}>
              <t.icon className="w-4 h-4" strokeWidth={1.5} /> {t.label}
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 16, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            {tab === "meditate" && <Meditation />}
            {tab === "breathe" && <Breathing />}
            {tab === "focus" && <Focus />}
          </motion.div>
        </AnimatePresence>
      </div>
    </Layout>
  );
}

function SoundPicker({ sound, setSound }) {
  return (
    <div className="flex justify-center gap-2 flex-wrap">
      <button onClick={() => { setSound(null); stopAmbient(); }} data-testid="sound-off"
        className={`px-4 py-2 rounded-full text-xs flex items-center gap-2 transition-all ${!sound ? "bg-[#4da6ff] text-white" : "btn-glass"}`}>
        <VolumeX className="w-3.5 h-3.5" /> Silent
      </button>
      {SOUNDS.map((s) => (
        <button key={s.id} onClick={() => { setSound(s.id); playAmbient(s.id); }} data-testid={`sound-${s.id}`}
          className={`px-4 py-2 rounded-full text-xs flex items-center gap-2 transition-all ${sound === s.id ? "bg-[#4da6ff] text-white" : "btn-glass"}`}>
          <s.icon className="w-3.5 h-3.5" /> {s.label}
        </button>
      ))}
    </div>
  );
}

function Meditation() {
  const [minutes, setMinutes] = useState(10);
  const [left, setLeft] = useState(600);
  const [running, setRunning] = useState(false);
  const [sound, setSound] = useState(null);
  const ref = useRef(null);

  useEffect(() => { if (!running) setLeft(minutes * 60); }, [minutes, running]);
  useEffect(() => {
    if (running) ref.current = setInterval(() => setLeft((l) => { if (l <= 1) { clearInterval(ref.current); setRunning(false); stopAmbient(); return 0; } return l - 1; }), 1000);
    return () => clearInterval(ref.current);
  }, [running]);
  useEffect(() => () => stopAmbient(), []);

  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  const pct = minutes > 0 ? (left / (minutes * 60)) * 100 : 0;

  return (
    <div className="glass-card p-12 max-w-xl mx-auto text-center" data-testid="meditation-panel">
      <div className="relative w-60 h-60 mx-auto mb-10">
        <div className="absolute inset-4 rounded-full bg-[#4da6ff]/10 blur-2xl animate-glow" />
        <svg className="w-full h-full -rotate-90 relative" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2.5" />
          <circle cx="50" cy="50" r="45" fill="none" stroke="#4da6ff" strokeWidth="2.5" strokeLinecap="round"
            strokeDasharray={283} strokeDashoffset={283 - (283 * pct) / 100} style={{ transition: "stroke-dashoffset 1s linear", filter: "drop-shadow(0 0 8px #4da6ff)" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-head text-5xl font-light" data-testid="meditation-time">{mm}:{ss}</div>
      </div>
      {!running && (
        <div className="flex justify-center gap-2 mb-7">
          {[5, 10, 15, 20, 30].map((m) => (
            <button key={m} onClick={() => setMinutes(m)} data-testid={`meditation-${m}min`}
              className={`px-4 py-2 rounded-full text-sm transition-all ${minutes === m ? "bg-[#4da6ff] text-white glow-blue" : "btn-glass"}`}>{m}m</button>
          ))}
        </div>
      )}
      <div className="flex justify-center gap-3 mb-8">
        <button onClick={() => { const next = !running; setRunning(next); if (next) { if (sound) playAmbient(sound); } else stopAmbient(); }} data-testid="meditation-timer-start"
          className="btn-primary px-8 py-3.5 font-medium flex items-center gap-2">
          {running ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />} {running ? "Pause" : "Begin"}
        </button>
        <button onClick={() => { setRunning(false); setLeft(minutes * 60); stopAmbient(); }} data-testid="meditation-reset" className="btn-glass px-5 py-3.5"><RotateCcw className="w-5 h-5" /></button>
      </div>
      <SoundPicker sound={sound} setSound={setSound} />
    </div>
  );
}

function Breathing() {
  const [pattern, setPattern] = useState("Box · 4-4-4-4");
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState(0);
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (!active) return;
    const durations = PATTERNS[pattern];
    let p = 0, c = durations[0];
    setPhase(0); setCount(c);
    ref.current = setInterval(() => {
      c -= 1;
      if (c <= 0) { do { p = (p + 1) % 4; } while (durations[p] === 0); c = durations[p]; setPhase(p); }
      setCount(c);
    }, 1000);
    return () => clearInterval(ref.current);
  }, [active, pattern]);

  const scale = phase === 0 || phase === 1 ? 1.45 : 0.7;

  return (
    <div className="glass-card p-12 max-w-xl mx-auto text-center" data-testid="breathing-panel">
      <div className="flex justify-center gap-2 mb-12 flex-wrap">
        {Object.keys(PATTERNS).map((p) => (
          <button key={p} onClick={() => { setPattern(p); setActive(false); }} data-testid={`breathing-pattern-${p.split(" ")[0].toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-xs transition-all ${pattern === p ? "bg-[#4da6ff] text-white glow-blue" : "btn-glass"}`}>{p}</button>
        ))}
      </div>
      <div className="relative h-72 flex items-center justify-center mb-10">
        <div className="absolute w-44 h-44 rounded-full bg-[#4da6ff]/10 blur-3xl animate-glow" />
        <motion.div animate={{ scale: active ? scale : 1 }} transition={{ duration: active ? (PATTERNS[pattern][phase] || 1) : 0.5, ease: "easeInOut" }}
          className="relative w-44 h-44 rounded-full flex items-center justify-center"
          style={{ background: "radial-gradient(circle at 35% 30%, rgba(77,166,255,0.35), rgba(77,166,255,0.05))", border: "1.5px solid rgba(77,166,255,0.5)", boxShadow: "0 0 70px rgba(77,166,255,0.4)" }} data-testid="breathing-circle">
          <div className="text-center">
            <div className="font-head text-xl font-light">{active ? PHASES[phase] : "Ready"}</div>
            {active && <div className="text-4xl font-head font-light mt-1">{count}</div>}
          </div>
        </motion.div>
      </div>
      <button onClick={() => setActive(!active)} data-testid="breathing-toggle" className="btn-primary px-8 py-3.5 font-medium">{active ? "Stop" : "Start Breathing"}</button>
    </div>
  );
}

function Focus() {
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const ref = useRef(null);

  useEffect(() => { if (open) ref.current = setInterval(() => setElapsed((e) => e + 1), 1000); return () => clearInterval(ref.current); }, [open]);
  useEffect(() => () => stopAmbient(), []);

  const enter = () => { setOpen(true); setElapsed(0); };
  const exit = () => { setOpen(false); stopAmbient(); setSound(null); };
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="glass-card p-12 max-w-xl mx-auto text-center" data-testid="focus-panel">
      <div className="w-14 h-14 rounded-2xl glass flex items-center justify-center mx-auto mb-6 glow-blue"><Maximize className="w-7 h-7 text-[#4da6ff]" strokeWidth={1.5} /></div>
      <h3 className="font-head text-2xl font-light mb-3 tracking-tight">Distraction-Free Focus</h3>
      <p className="text-zinc-500 mb-9 font-light leading-relaxed">Enter a full-screen void with rain, forest, or ocean ambience to lock into deep work.</p>
      <button onClick={enter} data-testid="focus-enter" className="btn-primary px-8 py-3.5 font-medium">Enter Focus Mode</button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#040407] flex flex-col items-center justify-center" data-testid="focus-fullscreen">
            <div className="absolute inset-0 overflow-hidden">
              <img src={BRAND.vortex} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#040407]/70 via-[#040407]/80 to-[#040407]" />
              <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-[#4da6ff]/10 blur-[150px] rounded-full animate-glow" />
              {[...Array(24)].map((_, i) => (
                <div key={i} className="absolute w-1 h-1 bg-[#4da6ff]/40 rounded-full animate-floaty"
                  style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 7}s` }} />
              ))}
            </div>
            <button onClick={exit} data-testid="focus-exit" className="absolute top-8 right-8 text-zinc-500 hover:text-white z-10 transition-colors"><X className="w-7 h-7" /></button>
            <div className="relative z-10 text-center">
              <div className="font-head text-8xl md:text-9xl font-extralight text-glow tracking-tight">{mm}:{ss}</div>
              <p className="text-zinc-500 mt-4 mb-14 tracking-[0.3em] text-xs uppercase">In Focus</p>
              <SoundPicker sound={sound} setSound={setSound} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
